import React from "react";

/**
 * Catches render-time exceptions so a single component error can never white-screen
 * the whole app or bounce the user back to the dashboard. Shows a recoverable UI.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Something went wrong" };
  }

  componentDidCatch(error, info) {
    // Keep a breadcrumb in the console for debugging (visible in dev).
    console.error("[EvidenceKeeper] Render error caught by ErrorBoundary:", error, info?.componentStack);
  }

  handleReset = () => this.setState({ hasError: false, message: "" });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center",
          padding: "2rem", background: "var(--ek-page, #060B14)", color: "var(--ek-text, #E6EDF6)",
        }}
      >
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <div style={{ fontFamily: "monospace", letterSpacing: "0.08em", fontSize: 12, color: "var(--ek-warning, #E4B34A)", marginBottom: 12 }}>
            SOMETHING INTERRUPTED THIS SCREEN
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>This section hit an unexpected error</h2>
          <p style={{ fontSize: 14, color: "var(--ek-text-secondary, #9FB0C3)", marginBottom: 16 }}>
            Your evidence and reports are safe. You can retry this screen, or use the navigation to continue — nothing was lost.
          </p>
          {process.env.NODE_ENV !== "production" && this.state.message && (
            <pre style={{ fontSize: 11, textAlign: "left", whiteSpace: "pre-wrap", background: "rgba(240,97,109,0.08)", border: "1px solid rgba(240,97,109,0.3)", borderRadius: 8, padding: 10, marginBottom: 16, color: "#F0616D" }}>
              {this.state.message}
            </pre>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              data-testid="error-retry-btn"
              onClick={this.handleReset}
              style={{ background: "var(--ek-primary, #2E82F6)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ background: "transparent", color: "var(--ek-text, #E6EDF6)", border: "1px solid var(--ek-border, #223)", borderRadius: 8, padding: "10px 18px", fontSize: 14, cursor: "pointer" }}
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
